import Typography from '@mui/material/Typography';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ControlledRadioGroup } from '../../../../shared/components';
import {
  COMPANY_NAME_OPTIONS,
  COMPANY_NAMES,
  OWNER_NAMES,
  SIGNATURE_OPTIONS,
} from '../../../domain/work.constants';

/**
 * Quotation letterhead + signature. Persisted on work_jobs2 as
 * `letterhead` / `signature` (V6). Extracted from StepBasicDetails when the
 * work job form was split along the work_jobs / work_jobs2 boundary.
 */
export default function QuotationSection() {
  const { watch, setValue } = useFormContext();
  const issuedBy = watch('details.quotation.issuedBy');
  const signedBy = watch('details.quotation.signedBy');

  useEffect(() => {
    if (issuedBy === COMPANY_NAMES.SCPL) {
      if (signedBy !== OWNER_NAMES.R_V_SHIRKE) {
        setValue('details.quotation.signedBy', OWNER_NAMES.R_V_SHIRKE, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [issuedBy, signedBy, setValue]);

  const signatureOptions = useMemo(() => {
    if (issuedBy === COMPANY_NAMES.SCPL) {
      return SIGNATURE_OPTIONS.map((opt) =>
        opt.value === OWNER_NAMES.S_S_DESARDA ? { ...opt, disabled: true } : opt,
      );
    }

    return SIGNATURE_OPTIONS;
  }, [issuedBy]);

  return (
    <div className='column gap-1'>
      <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>
        Quotation
      </Typography>
      <div className='row gap-8 indent-1'>
        <ControlledRadioGroup
          name='details.quotation.issuedBy'
          orientation='column'
          label='By the name of'
          options={COMPANY_NAME_OPTIONS}
          wrapperSx={{ gap: 1.5, width: 'auto' }}
          sx={{ gap: 1 }}
          rules={{ required: 'Please select a quotation letterhead' }}
          reserveErrorSpace
        />
        <ControlledRadioGroup
          name='details.quotation.signedBy'
          orientation='column'
          label='Signature'
          options={signatureOptions}
          wrapperSx={{ gap: 1.5, width: 'auto' }}
          sx={{ gap: 1 }}
          reserveErrorSpace
          rules={{
            validate: (_, values) => {
              const company = values.quotation?.issuedBy;
              const signer = values.quotation?.signedBy;

              if (!company) return true;

              if (company === COMPANY_NAMES.SCPL && signer !== OWNER_NAMES.R_V_SHIRKE) {
                return 'Signature mismatch';
              }

              return true;
            },
          }}
          disabled={!issuedBy}
        />
      </div>
    </div>
  );
}
